import { Engine, Entity } from "excalibur";
import { FC, useEffect, useMemo, useState } from "react";
import { hasHudInterface } from "../hud";
import { MyLevel } from "../level";
import { Card } from "../types";
import { HandCombiningInfo } from "../deck";
import HandDisplay from "./HandDisplay";
import { hasTargetManager, TargetableComponent } from "../ecs/target";

export interface HandDisplayContainerProps {
  game: Engine;
}

interface DeckState {
  hand: Card[];
  drawPileSize: number;
  maxHandSize: number;
  canDraw: boolean;
  combiningInfo?: HandCombiningInfo;
}

interface UndoState {
  canUndo: boolean;
  undoDescription?: string;
}

const HandDisplayContainer: FC<HandDisplayContainerProps> = ({ game }) => {
  const [deckState, setDeckState] = useState<DeckState>({
    hand: [],
    drawPileSize: 0,
    maxHandSize: 5,
    canDraw: true,
    combiningInfo: undefined,
  });

  const [undoState, setUndoState] = useState<UndoState>({
    canUndo: false,
    undoDescription: undefined,
  });

  const [hoverTarget, setHoverTarget] = useState<
    Entity<TargetableComponent> | undefined
  >();

  const hudController = useMemo(
    () =>
      (hasHudInterface(game.currentScene) && game.currentScene.hudInterface) ||
      undefined,
    [game.currentScene],
  );

  useEffect(() => {
    const updateInterval = setInterval(() => {
      const currentScene = game.currentScene;
      if (currentScene instanceof MyLevel) {
        // Update target tracking
        if (hasTargetManager(currentScene)) {
          setHoverTarget(currentScene.targetManager.hoverTarget);
        }

        setDeckState({
          hand: currentScene.cards.cardsInHand,
          drawPileSize: currentScene.cards.drawPileSize(),
          maxHandSize: currentScene.cards.maxHandSize,
          canDraw: currentScene.cards.canDraw(),
          combiningInfo: currentScene.cards.getHandCombiningInfo(),
        });

        // Get fresh hudController reference
        const currentHudController =
          (hasHudInterface(currentScene) && currentScene.hudInterface) ||
          undefined;

        setUndoState({
          canUndo: currentHudController?.canUndo() ?? false,
          undoDescription: currentHudController?.lastUndoDescription(),
        });
      }
    }, 100);

    return () => clearInterval(updateInterval);
  }, [game]);

  const handleUndo = () => {
    hudController?.undoLastAction();
  };

  const handleShopClick = () => {
    hudController?.shopClicked();
  };

  return (
    <HandDisplay
      hand={deckState.hand}
      drawPileSize={deckState.drawPileSize}
      maxHandSize={deckState.maxHandSize}
      canDraw={deckState.canDraw}
      onDrawCard={() => hudController?.drawCard()}
      swapCards={(a, b) => hudController?.swapCardsInHand(a, b)}
      moveCard={(cardId, newIndex) => hudController?.moveCardInHand(cardId, newIndex)}
      onUndo={handleUndo}
      canUndo={undoState.canUndo}
      undoDescription={undoState.undoDescription}
      combiningInfo={deckState.combiningInfo}
      onShopClick={handleShopClick}
      hoverTarget={hoverTarget}
    />
  );
};

export default HandDisplayContainer;
